import React from "react";
import { ArrowLeft } from "lucide-react";
import {
  DIRECT_STATE_OPTIONS,
  STATE_LEVELS,
} from "@/components/daily/dailyState";

export const AppPageHeader = ({ eyebrow, title, body, action, onBack }) => (
  <div className="mb-5">
    <div className="flex items-start justify-between gap-4">
      <div className="min-w-0">
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            className="mb-4 inline-flex items-center gap-2 text-xs forr-text-teal-deep"
          >
            <ArrowLeft size={15} /> Back
          </button>
        )}
        <div className="forr-overline">{eyebrow}</div>
        <h1 className="mt-2 font-display text-3xl font-medium tracking-[-0.025em] forr-text-ink sm:text-4xl">
          {title}
        </h1>
        {body && (
          <p className="mt-3 max-w-2xl text-sm leading-relaxed forr-text-soft sm:text-base">
            {body}
          </p>
        )}
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
  </div>
);

export const AppCard = ({ children, className = "", tone = "surface", ...props }) => {
  const toneClass =
    tone === "action"
      ? "forr-panel-action"
      : tone === "information"
        ? "forr-panel-information"
        : "forr-surface";
  return (
    <div
      {...props}
      className={`rounded-[24px] p-5 sm:p-6 ${toneClass} ${className}`}
      style={{ border: "1px solid var(--forr-line-soft)", ...(props.style || {}) }}
    >
      {children}
    </div>
  );
};

export const StatCard = ({ icon: Icon, value, label, detail }) => (
  <div
    className="rounded-[19px] p-4 forr-surface"
    style={{ border: "1px solid var(--forr-line-soft)" }}
  >
    <div className="flex items-center gap-3">
      {Icon && (
        <div
          className="flex h-9 w-9 items-center justify-center rounded-2xl"
          style={{
            backgroundColor: "var(--forr-teal-tint)",
            color: "var(--forr-teal-deep)",
          }}
        >
          <Icon size={17} />
        </div>
      )}
      <div>
        <div className="font-display text-2xl forr-text-ink">{value}</div>
        <div className="text-[10px] uppercase tracking-[0.16em] forr-text-mute">
          {label}
        </div>
      </div>
    </div>
    {detail && <p className="mt-3 text-xs leading-relaxed forr-text-mute">{detail}</p>}
  </div>
);

export const ChoiceRow = ({ options, value, onChange }) => (
  <div className="mt-3 flex flex-wrap gap-2">
    {options.map((option) => {
      const optionValue = option.key ?? option.value;
      const selected = value === optionValue;
      return (
        <button
          type="button"
          key={optionValue}
          aria-pressed={selected}
          onClick={() => onChange(optionValue)}
          className="rounded-full px-4 py-2.5 text-sm transition-colors"
          style={
            selected
              ? {
                  backgroundColor: "var(--forr-teal-soft)",
                  border: "1px solid var(--forr-teal)",
                  color: "var(--forr-ink)",
                }
              : {
                  backgroundColor: "var(--forr-surface)",
                  border: "1px solid var(--forr-line-soft)",
                  color: "var(--forr-ink-soft)",
                }
          }
        >
          {option.label}
        </button>
      );
    })}
  </div>
);

export const StateSelector = ({ value, onChange, compact = false }) => (
  <div className="grid grid-cols-5 gap-2">
    {STATE_LEVELS.map((level) => {
      const selected = value === level.value;
      return (
        <button
          type="button"
          key={level.value}
          aria-pressed={selected}
          onClick={() => onChange(level.value)}
          className={`rounded-[18px] text-center transition-all ${
            compact ? "p-2" : "px-1.5 py-3"
          }`}
          style={{
            backgroundColor: selected ? "var(--forr-teal-tint)" : "var(--forr-surface)",
            border: `1px solid ${selected ? "var(--forr-teal)" : "var(--forr-line-soft)"}`,
            transform: selected ? "translateY(-2px)" : "none",
          }}
        >
          <span
            className={`block font-display forr-text-teal-deep ${compact ? "text-lg" : "text-2xl"}`}
          >
            {level.value}
          </span>
          {!compact && (
            <span className="mt-1 block text-[9px] font-medium leading-tight forr-text-ink sm:text-[10px]">
              {level.label}
            </span>
          )}
        </button>
      );
    })}
  </div>
);

export const StateCards = ({ primary, secondary, onPrimary, onSecondary }) => (
  <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
    {DIRECT_STATE_OPTIONS.map((option) => {
      const selected = primary === option.key || secondary === option.key;
      const isPrimary = primary === option.key;
      return (
        <button
          type="button"
          key={option.key}
          aria-pressed={selected}
          onClick={() => {
            if (isPrimary) onPrimary("");
            else if (!primary) onPrimary(option.key);
            else if (secondary === option.key) onSecondary("");
            else if (primary !== option.key) onSecondary(option.key);
          }}
          className="rounded-2xl p-3.5 text-left transition-all"
          style={{
            backgroundColor: selected ? "var(--forr-teal-soft)" : "var(--forr-surface)",
            border: `1px solid ${selected ? "var(--forr-teal)" : "var(--forr-line-soft)"}`,
          }}
        >
          <div className="text-sm font-medium forr-text-ink">{option.label}</div>
          <div className="mt-1 text-[10px] leading-snug forr-text-mute">{option.system}</div>
          {selected && (
            <div className="mt-2 text-[9px] uppercase tracking-[0.16em] forr-text-teal-deep">
              {isPrimary ? "Primary" : "Secondary"}
            </div>
          )}
        </button>
      );
    })}
  </div>
);
